import React, { createContext, useState, useEffect, useCallback } from "react";
import { authService } from "../services/auth.service";
import { tokenManager } from "../services/tokenManager";
import { storage } from "../storage/storage";
import type { User, LoginPayload, RegisterPayload } from "../types/auth.types";

const USER_KEY = "user";

interface AuthContextValue {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (payload: LoginPayload) => Promise<void>;
    register: (payload: RegisterPayload) => Promise<void>;
    logout: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    // Restore user from localStorage immediately (no API call needed)
    const [user, setUser] = useState<User | null>(() => {
        const hasTokens = tokenManager.getAccessToken() || tokenManager.getRefreshToken();
        if (!hasTokens) return null;
        return storage.get<User>(USER_KEY);
    });
    const [isLoading, setIsLoading] = useState(!user);

    const persistUser = (u: User | null) => {
        setUser(u);
        if (u) {
            storage.set(USER_KEY, u);
        } else {
            storage.remove(USER_KEY);
        }
    };

    // Background validation: silently refresh profile & tokens
    useEffect(() => {
        const validate = async () => {
            const hasTokens = tokenManager.getAccessToken() || tokenManager.getRefreshToken();
            if (!hasTokens) {
                persistUser(null);
                setIsLoading(false);
                return;
            }

            try {
                const profile = await authService.getProfile();
                persistUser(profile);
            } catch {
                const refreshTkn = tokenManager.getRefreshToken();
                if (!refreshTkn) {
                    tokenManager.clearTokens();
                    persistUser(null);
                } else {
                    try {
                        const res = await authService.refresh();
                        tokenManager.setTokens(res.data.accessToken, res.data.refreshToken);
                        const profile = await authService.getProfile();
                        persistUser(profile);
                    } catch {
                        tokenManager.clearTokens();
                        persistUser(null);
                    }
                }
            } finally {
                setIsLoading(false);
            }
        };

        validate();
    }, []);

    const login = useCallback(async (payload: LoginPayload) => {
        const res = await authService.login(payload);
        tokenManager.setTokens(res.data.accessToken, res.data.refreshToken);
        persistUser(res.data.user);
    }, []);

    const register = useCallback(async (payload: RegisterPayload) => {
        const res = await authService.register(payload);
        tokenManager.setTokens(res.data.accessToken, res.data.refreshToken);
        persistUser(res.data.user);
    }, []);

    const logout = useCallback(async () => {
        try {
            await authService.logout();
        } catch {
            // Ignore logout API errors
        } finally {
            tokenManager.clearTokens();
            persistUser(null);
        }
    }, []);

    return (
        <AuthContext.Provider
            value={{
                user,
                isAuthenticated: !!user,
                isLoading,
                login,
                register,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}
